<template>
  <div>
    <div class="d-flex flex-column mb-6">
      <span class="font-weight-bold font-size-20">
        Договори — погодження з БЕСТ24
      </span>
      <span class="font-size-13 text-medium-emphasis">
        Маршрути візування договорів, що надходять із системи БЕСТ24
      </span>
    </div>

    <b-section-loader
      :loading="$loading.isLoading('documentAgreementDashboard')">
      <v-card>
        <v-card-text>
          <div class="d-flex align-center justify-space-between mb-5">
            <div class="font-weight-bold">Останні договори на погодженні</div>
            <v-btn
              border
              color="white"
              prepend-icon="mdi-format-list-bulleted"
              @click="openAllAgreements">
              <span class="text-none font-weight-light">Усі договори</span>
            </v-btn>
          </div>

          <div v-if="latestAgreements.length" class="d-flex flex-column ga-3">
            <v-card v-for="agreement in latestAgreements" :key="agreement.id">
              <v-card-text
                class="rounded-lg border cursor-pointer"
                @click="openAgreement(agreement.id)">
                <div class="d-flex align-center ga-4">
                  <div class="font-weight-bold text-disabled text-no-wrap">
                    {{ agreement.number || '---' }}
                  </div>

                  <div class="flex-grow-1 overflow-hidden">
                    <div class="font-weight-bold text-truncate">
                      {{ agreement.name || '---' }}
                    </div>
                    <div class="text-disabled text-truncate">
                      {{ agreement.contractor?.shortName || '---' }}
                      {{ agreement.department?.name || '---' }}
                    </div>
                  </div>

                  <div class="d-flex align-center ga-4">
                    <span
                      v-if="getAgreementProgress(agreement)"
                      class="font-weight-bold text-secondary text-no-wrap">
                      {{ getAgreementProgress(agreement) }}
                    </span>

                    <v-chip
                      :color="getAgreementStatusColor(agreement)"
                      class="font-weight-bold"
                      variant="tonal">
                      {{ getAgreementStatusTitle(agreement) }}
                    </v-chip>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <div
            v-else
            class="d-flex flex-column align-center justify-center text-disabled py-10">
            <v-icon class="mb-2" size="32">mdi-file-document-outline</v-icon>
            <div class="font-weight-bold">Список пустий</div>
            <div>Договорів на погодженні немає</div>
          </div>
        </v-card-text>
      </v-card>
    </b-section-loader>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { BSectionLoader } from 'best-modules/components'
import { handleAsync } from 'best-modules/plugins'
import { documentAgreementDashboard } from '@/requests/documentAgreement'
import { DefaultItem } from 'best-modules/plugins/directory/types'

defineOptions({
  name: 'DocumentAgreementDashboard',
})

type DashboardAgreement = {
  id: number
  number: string
  name: string
  contractor?: {
    id: number
    name: string
    shortName?: string | null
  }
  department: DefaultItem
  statusId: number
  routeProgress?: {
    current: number
    total: number
  }
}

const router = useRouter()
const latestAgreements = ref<DashboardAgreement[]>([])

const getDashboard = () => {
  return handleAsync('documentAgreementDashboard', async () => {
    try {
      const response = await documentAgreementDashboard()

      latestAgreements.value = response.latestAgreements || []
    } catch (error) {
      latestAgreements.value = []
    }
  })
}

const openAllAgreements = () => {
  router.push({ name: 'document-agreement-me' })
}

const openAgreement = (id: number) => {
  router.push({ name: 'document-agreement-single', params: { id } })
}

const getAgreementProgress = (agreement: DashboardAgreement) => {
  if (!agreement.routeProgress) return ''

  return `Віза ${agreement.routeProgress.current}/${agreement.routeProgress.total}`
}

const getAgreementStatusColor = (agreement: DashboardAgreement) => {
  if (agreement.statusId === 1) return 'secondary'
  if (agreement.statusId === 2) return 'error'
  if (agreement.statusId === 3) return 'deep-purple-accent-3'
  if (agreement.statusId === 4) return 'success'

  return 'warning'
}

const getAgreementStatusTitle = (agreement: DashboardAgreement) => {
  if (agreement.statusId === 1) return 'Новий'
  if (agreement.statusId === 2) return 'Доопрацювання'
  if (agreement.statusId === 3) return 'На візуванні'
  if (agreement.statusId === 4) return 'Погоджено'

  return '---'
}

onMounted(() => {
  getDashboard()
})
</script>
